import { TcModelAlignment } from '@data-access/connect/tc-tcps';
import {
  CreateRotation,
  createTransform,
  cross,
  normalize,
  scale,
  vector3,
  Vector3,
} from '@fulcrum/lib';
import { Matrix4 } from '@technology/web3d';
import { convertConnectPointToViewerPoint } from '../views/camera-conversion';

const MAX_ASIN_INPUT = 1;
const MIN_ASIN_INPUT = -1;

type TcModelAlignmentAxes = Pick<
  TcModelAlignment,
  | 'axisX'
  | 'axisY'
  | 'axisZ'
  | 'refDirectionX'
  | 'refDirectionY'
  | 'refDirectionZ'
>;

export function convertConnectModelAlignmentToViewerTransform(
  alignment: TcModelAlignment
): Matrix4 {
  const t = convertConnectPointToViewerPoint(
    alignment.locationX,
    alignment.locationY,
    alignment.locationZ
  );
  const { x, y, z } = getModelAlignmentBasis(alignment);
  const s = alignment.scale;

  return createTransform(scale(x, s), scale(y, s), scale(z, s), t) as Matrix4;
}

export function convertConnectModelAlignmentToViewerEuler(
  alignment: TcModelAlignment
): Vector3 {
  const { x, y, z } = getModelAlignmentBasis(alignment);
  const rotationY = Math.asin(clamp(-x.z, MIN_ASIN_INPUT, MAX_ASIN_INPUT));
  const rotationX = Math.atan2(y.z, z.z);
  const rotationZ = Math.atan2(x.y, x.x);

  return vector3(rotationX, rotationY, rotationZ);
}

export function convertViewerEulerToConnectModelAlignmentAxes(
  viewerEuler: Vector3
): TcModelAlignmentAxes {
  const transform = CreateRotation(viewerEuler.x, viewerEuler.y, viewerEuler.z);

  return {
    axisX: transform.axisZ.x,
    axisY: transform.axisZ.y,
    axisZ: transform.axisZ.z,
    refDirectionX: transform.axisX.x,
    refDirectionY: transform.axisX.y,
    refDirectionZ: transform.axisX.z,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getModelAlignmentBasis(alignment: TcModelAlignment): {
  x: Vector3;
  y: Vector3;
  z: Vector3;
} {
  const z = normalize(
    vector3(alignment.axisX, alignment.axisY, alignment.axisZ)
  );
  const refDirection = vector3(
    alignment.refDirectionX,
    alignment.refDirectionY,
    alignment.refDirectionZ
  );
  const y = normalize(cross(z, refDirection));
  const x = normalize(cross(y, z));

  return { x, y, z };
}
